import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import toast from '$$uiKitComp/toast';
import app from '$$app';
import {
  buildAxiosErrorMeta,
  buildBrowserClientContext,
  buildWebixAjaxErrorMeta,
  resolveRequestPath,
} from './buildAxiosErrorMeta';
import { createApiErrorToasts } from './apiErrorToasts';
import { notifyErrorSafely } from '../helpers/userExtAnalytics/bugsnag';

const internalApi = axios.create({
  baseURL: '/api/internal',
});

const api = axios.create({
  baseURL: '/api',
});

const socketInterceptor = req => {
  const methods = ['post', 'put', 'delete'];

  if (methods.includes(req.method)) {
    req.headers.socketId = app.socket?.id || '';
  }

  return req;
};

const requestIdInterceptor = req => {
  const requestId = uuidv4();

  req.headers['X-Request-Id'] = requestId;
  req.meta = req.meta || {};
  req.meta.requestId = requestId;

  return req;
};

const metaInterceptor = req => {
  req.meta = req.meta || {};
  req.meta.requestStartedAt = new Date().getTime();

  if (req.data) {
    req.meta.data = req.data;
  }

  return req;
};

api.interceptors.request.use(requestIdInterceptor);
internalApi.interceptors.request.use(requestIdInterceptor);
api.interceptors.request.use(metaInterceptor);
internalApi.interceptors.request.use(metaInterceptor);

api.interceptors.request.use(socketInterceptor);
internalApi.interceptors.request.use(socketInterceptor);

// Path-only (no method): a recent PUT 200 explains a following POST Network Error on the same path.
const RECENT_OK_MAX_KEYS = 50;
const recentEndpointOkAt = new Map();

function rememberEndpointOk(config) {
  const path = resolveRequestPath(config);

  if (!path) {
    return;
  }
  if (recentEndpointOkAt.size >= RECENT_OK_MAX_KEYS && !recentEndpointOkAt.has(path)) {
    const oldest = recentEndpointOkAt.keys().next().value;

    recentEndpointOkAt.delete(oldest);
  }
  recentEndpointOkAt.set(path, Date.now());
}

function recentSameEndpointOkMsAgo(config) {
  const path = resolveRequestPath(config);

  if (!path) {
    return undefined;
  }
  const okAt = recentEndpointOkAt.get(path);

  if (okAt == null) {
    return undefined;
  }

  return Date.now() - okAt;
}

function buildStoreApiClientContext(error) {
  const ctx = buildBrowserClientContext();

  if (app.socket && typeof app.socket.connected === 'boolean') {
    ctx.socketConnected = app.socket.connected;
  }

  const recentOkMsAgo = recentSameEndpointOkMsAgo(error.config);

  if (typeof recentOkMsAgo === 'number') {
    ctx.recentSameEndpointOkMsAgo = recentOkMsAgo;
  }

  return ctx;
}

function enrichAndThrowStoreApiError(error, clientKey) {
  const { meta, errorName, capturedMessage } = buildAxiosErrorMeta(
    error,
    clientKey,
    buildStoreApiClientContext(error),
  );

  error._meta = meta;
  error._errorName = errorName;
  error._capturedMessage = capturedMessage;
  throw error;
}

function trackOkResponse(response) {
  rememberEndpointOk(response.config);

  return response;
}

// User-facing error toasts: three failure classes (NETWORK / GATEWAY / SERVER), each
// surfaced by a single gated sticky toast so a burst never stacks duplicates. Logic lives
// in ./apiErrorToasts (pure + unit-tested); one shared instance across both clients so the
// gates are global. Independently, every error is enriched + re-thrown to Bugsnag below.
const apiErrorToasts = createApiErrorToasts(toast, __);

internalApi.interceptors.response.use(trackOkResponse,
  error => {
    apiErrorToasts.onInternalApiError(error);

    enrichAndThrowStoreApiError(error, 'internalApi');
  });

api.interceptors.response.use(trackOkResponse,
  error => {
    apiErrorToasts.onApiError(error);

    enrichAndThrowStoreApiError(error, 'api');
  });

// old webix support

const debouncedToastCall = _.debounce(locale => {
  toast.error(locale ? __(locale) : __('error_tasks_api_operation'));
}, 300);

// Legacy webix.ajax calls bypass the axios interceptors above, so their failures
// never reach Bugsnag on their own. Report them here with the same route grouping.
function pickXhrLike(request, rejection) {
  if (request && typeof request.status === 'number') return request;
  if (rejection && typeof rejection.status === 'number') return rejection;

  return null;
}

function reportWebixAjaxError(mode, url, request, rejection) {
  try {
    const xhr = pickXhrLike(request, rejection);
    const { errorName, message, meta } = buildWebixAjaxErrorMeta(mode, url, xhr);

    notifyErrorSafely(new Error(message), { WEBIXAJAX: meta }, errorName);
  } catch (e) {
    // Reporting must never break the request flow.
    console.error('[api] reportWebixAjaxError failed:', e);
  }
}

webix.attachEvent('onBeforeAjax', (mode, url, data, request, headers, files, promise) => {
  headers.socketId = app.socket.id || '';
  promise.then(() => {}, rejection => {
    debouncedToastCall();
    reportWebixAjaxError(mode, url, request, rejection);
  });
});

export {
  internalApi,
  api,
};
